import { TICKET_FEES, type TicketFeeTier } from '@pulsify/shared'
import {
  type BillingPeriod,
  SUBSCRIPTION_TIER_CREDITS,
  SUBSCRIPTION_TIER_LIMITS,
  SUBSCRIPTION_TIERS,
  type SubscriptionTier,
} from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'

/** Mirrors the backend PERIOD_RANK: a higher rank is a longer commitment. */
export const BILLING_PERIOD_RANK: Record<BillingPeriod, number> = {
  monthly: 0,
  quarterly: 1,
  yearly: 2,
}

/** Periods a customer can move to for a lower yearly total, cheapest last. */
export const CHEAPER_BILLING_PERIODS: Record<
  BillingPeriod,
  readonly Exclude<BillingPeriod, 'monthly'>[]
> = {
  monthly: ['quarterly', 'yearly'],
  quarterly: ['yearly'],
  yearly: [],
}

export const LARGE_CUSTOMER_EMAIL = 'info@pulsify.at'

export function formatCredits(amount: number): string {
  return new Intl.NumberFormat(getLocale()).format(amount)
}

export function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100)
}

export function billingPeriodLabel(period: BillingPeriod): string {
  if (period === 'monthly') return m.monthly()
  if (period === 'quarterly') return m.quarterly()
  return m.yearly()
}

export function billingPeriodSuffix(period: BillingPeriod): string {
  if (period === 'monthly') return m.perMonth()
  if (period === 'quarterly') return m.perQuarter()
  return m.perYear()
}

export function yearlySavingsLabel(
  period: Exclude<BillingPeriod, 'monthly'>,
  amount: string,
): string {
  return period === 'quarterly'
    ? m.savePerYearWithQuarterly({ amount })
    : m.savePerYearWithYearly({ amount })
}

export function planName(tier: SubscriptionTier): string {
  if (tier === 'verein_s') return m.planNameVereinS()
  if (tier === 'verein_m') return m.planNameVereinM()
  if (tier === 'basic') return m.planNamePro()
  if (tier === 'premium') return m.planNamePremium()
  return m.planNameFree()
}

export function feeRuleLabel(
  fee: { basisPoints: number; fixedCents: number },
  locale: string = getLocale(),
): string {
  const percent = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(fee.basisPoints / 10_000)
  const fixed = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
    fee.fixedCents / 100,
  )
  return `${percent} + ${fixed}`
}

function ticketFeeLabel(tier: TicketFeeTier): string {
  return feeRuleLabel(TICKET_FEES[tier])
}

type PlanValue = string | boolean

interface PlanFeature {
  category: string
  features: { name: string; values: Record<SubscriptionTier, PlanValue> }[]
}

interface PlanConfig {
  tier: SubscriptionTier
  name: string
  description: string
  highlighted?: boolean
}

const PLAN_DESCRIPTIONS: Record<SubscriptionTier, () => string> = {
  free: m.forGettingStarted,
  verein_s: m.planVereinSDescription,
  verein_m: m.planVereinMDescription,
  basic: m.forGrowingBusinesses,
  premium: m.forEstablishedBusinesses,
}

export function getPlans(): PlanConfig[] {
  return SUBSCRIPTION_TIERS.map((tier) => ({
    tier,
    name: planName(tier),
    description: PLAN_DESCRIPTIONS[tier](),
    highlighted: tier === 'basic',
  }))
}

const RETIRED_TIERS: readonly SubscriptionTier[] = ['verein_m']

export function offeredPlans(
  plans: PlanConfig[],
  pricedTiers: readonly string[] | null,
  currentTier: SubscriptionTier,
): PlanConfig[] {
  const sellable = plans.filter(
    (plan) => plan.tier === currentTier || !RETIRED_TIERS.includes(plan.tier),
  )
  if (!pricedTiers || pricedTiers.length === 0) return sellable
  return sellable.filter(
    (plan) => plan.tier === 'free' || plan.tier === currentTier || pricedTiers.includes(plan.tier),
  )
}

export const planLimits: Record<
  SubscriptionTier,
  { canRecurEvents: boolean; maxListings: number; highlightsMembers: boolean }
> = {
  free: { canRecurEvents: false, maxListings: 1, highlightsMembers: true },
  verein_s: { canRecurEvents: true, maxListings: 3, highlightsMembers: true },
  verein_m: { canRecurEvents: true, maxListings: 3, highlightsMembers: true },
  basic: { canRecurEvents: true, maxListings: 3, highlightsMembers: false },
  premium: { canRecurEvents: true, maxListings: -1, highlightsMembers: false },
}

function byTier(value: (tier: SubscriptionTier) => PlanValue): Record<SubscriptionTier, PlanValue> {
  return Object.fromEntries(SUBSCRIPTION_TIERS.map((tier) => [tier, value(tier)])) as Record<
    SubscriptionTier,
    PlanValue
  >
}

function activeEventsLabel(tier: SubscriptionTier): string {
  const max = SUBSCRIPTION_TIER_LIMITS[tier].maxActiveEvents
  return max < 0 ? m.unlimitedActiveEvents() : m.activeEventsCount({ count: String(max) })
}

function draftsLabel(tier: SubscriptionTier): string {
  const max = SUBSCRIPTION_TIER_LIMITS[tier].maxDraftEvents
  return max < 0 ? m.unlimitedDrafts() : m.draftsCount({ count: String(max) })
}

function planningHorizonLabel(tier: SubscriptionTier): string {
  const days = SUBSCRIPTION_TIER_LIMITS[tier].maxAdvanceDays
  return days < 0 ? m.planningHorizonUnlimited() : m.planningHorizonDays({ count: String(days) })
}

function imagesLabel(tier: SubscriptionTier): string {
  return m.maxImages({ count: String(SUBSCRIPTION_TIER_LIMITS[tier].maxImages) })
}

export function getFeatureCategories(): PlanFeature[] {
  return [
    {
      category: m.planCredits(),
      features: [
        {
          name: m.creditsMonthly(),
          values: byTier((tier) =>
            m.creditsAmount({ count: formatCredits(SUBSCRIPTION_TIER_CREDITS[tier]) }),
          ),
        },
      ],
    },
    {
      category: m.events(),
      features: [
        { name: m.parallelActiveEvents(), values: byTier(activeEventsLabel) },
        { name: m.drafts(), values: byTier(draftsLabel) },
        { name: m.planningHorizon(), values: byTier(planningHorizonLabel) },
        { name: m.recurringEvents(), values: byTier((tier) => planLimits[tier].canRecurEvents) },
      ],
    },
    {
      category: m.ticketsTitle(),
      features: [{ name: m.ticketsFeeTitle(), values: byTier(ticketFeeLabel) }],
    },
    {
      category: m.media(),
      features: [{ name: m.planImagesRow(), values: byTier(imagesLabel) }],
    },
    {
      category: m.membersTitle(),
      features: [{ name: m.planMembersRow(), values: byTier(() => true) }],
    },
    {
      category: m.analytics(),
      features: [
        { name: m.impressions(), values: byTier(() => true) },
        { name: m.follower(), values: byTier(() => true) },
        { name: m.likesOnEvents(), values: byTier(() => true) },
        { name: m.profileViews(), values: byTier(() => true) },
      ],
    },
  ]
}

/**
 * The profile whose plan a cancel, resume or portal action targets: the active profile only when
 * the plan shown is its own; otherwise the organization's plan it inherits.
 */
export function managedPlanProfileId(
  activeProfileId: string | undefined,
  ownPlan: boolean,
): string | undefined {
  return ownPlan ? activeProfileId : undefined
}

/** The few lines a plan card shows; the full list lives in the comparison table. */
export function getPlanHighlights(tier: SubscriptionTier): string[] {
  return [
    m.planCreditsPerMonth({ count: formatCredits(SUBSCRIPTION_TIER_CREDITS[tier]) }),
    activeEventsLabel(tier),
    imagesLabel(tier),
    m.planTicketFee({ fee: ticketFeeLabel(tier) }),
    ...(planLimits[tier].highlightsMembers ? [m.planMembersHighlight()] : []),
  ]
}
